import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { DashboardReviewContext, RootState, Subtask, Task } from 'constants/interfaces'
import { AppDispatch } from 'config/redux'
import { getTask } from 'redux/actions/tasks_v2'
import { EMPTY_REVIEW_FILTER, filterReviewSubtasks, ReviewEntry, ReviewFilter } from '../model/dashboardReview'
import { findResultForMutation } from 'components/TasksV2/model/resultRequests'

import type { ExecutionCommentDraft } from 'components/TasksV2/CommentsSection/ExecutionCommentComposer'
import type { AnswerDraft } from '../model/execution'

interface IReviewState {
  commentDrafts: React.MutableRefObject<Record<string, ExecutionCommentDraft>>
  commentDraftResets: Record<string, number>
  resetCommentDraft: (key: string) => void
  answerDrafts: React.MutableRefObject<Record<string, Record<string, AnswerDraft>>>

  task: Task
  contexts: DashboardReviewContext[]
  filter: ReviewFilter
  setFilter: (filter: ReviewFilter) => void
  subtasks: Subtask[]
  selectedId: string | null
  setSelected: (id: string | null, media: boolean) => void
  entry: ReviewEntry
  loading: boolean
  error: boolean
  switchContext: (contextId: string) => void
  retry: () => void
  statusFor: (contextId: string, subtaskId?: string | null) => number | undefined
}

const DashboardReviewContextValue = createContext<IReviewState | null>(null)
export const useDashboardReview = () => useContext(DashboardReviewContextValue)

export const DashboardReviewProvider: React.FC<{
  task: Task
  contexts: DashboardReviewContext[]
  initialFilter?: ReviewFilter
  initialEntry?: ReviewEntry
}> = ({ task: initialTask, contexts, initialFilter = EMPTY_REVIEW_FILTER, initialEntry, children }) => {
  const dispatch: AppDispatch = useDispatch()
  const [task, setTask] = useState(initialTask)
  const [filter, setFilter] = useState<ReviewFilter>(initialFilter)
  const [selection, setSelection] = useState<ReviewEntry>(initialEntry || { id: null, media: false })
  const [entry, setEntry] = useState(selection)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(false)
  const [commentDraftResets, setCommentDraftResets] = useState<Record<string, number>>({})
  const commentDrafts = useRef<Record<string, ExecutionCommentDraft>>({})
  const answerDrafts = useRef<Record<string, Record<string, AnswerDraft>>>({})
  const request = useRef(0)
  const pendingContext = useRef<string | null>(null)
  const mounted = useRef(true)
  const source = useSelector((state: RootState) => state.tasks_v2.subtasks[task.tid] || [])
  const results = useSelector((state: RootState) => state.tasks_v2.allResults[task.tid] || [])
  const subtasks = useMemo(() => filterReviewSubtasks(source, filter), [source, filter])

  const setSelected = (id: string | null, media: boolean) =>
    setSelection((previous) => (previous.id === id && previous.media === media ? previous : { id, media }))

  const resetCommentDraft = (key: string) => {
    if (mounted.current) setCommentDraftResets((versions) => ({ ...versions, [key]: (versions[key] || 0) + 1 }))
  }

  const switchContext = async (contextId: string) => {
    const target = contexts.find((item) => item.contextId === contextId)
    if (!target || (contextId === task.dashboardContextId && !error)) return
    const token = ++request.current
    const isCurrent = () => mounted.current && request.current === token
    pendingContext.current = contextId
    setLoading(true)
    setError(false)
    const response = await dispatch(
      getTask({
        tid: task.tid,
        task_context_id: target.taskContextId,
        result_context_id: target.contextId,
        recurrence_ts: task.recurrence_ts || null,
        shouldApply: isCurrent,
      })
    )
    if (!isCurrent()) return
    if (response.task) {
      const available = filterReviewSubtasks(response.subtasks as Subtask[], filter)
      const id = available.find((item) => item.st_id === selection.id)?.st_id || available[0]?.st_id || null
      setEntry({ id, media: selection.media })
      setSelection({ id, media: selection.media })
      setTask({ ...response.task, dashboardContextId: contextId, recurrence_ts: task.recurrence_ts })
    } else {
      setError(true)
    }
    setLoading(false)
  }

  const statusFor = (contextId: string, subtaskId?: string | null) => {
    const row = contexts.find((item) => item.contextId === contextId)
    if (!subtaskId) return row?.status
    const result = findResultForMutation(results, subtaskId, contextId, task.recurrence_ts || null)
    return result?.status ?? row?.statuses[subtaskId]
  }

  useEffect(
    () => () => {
      mounted.current = false
      request.current += 1
      Object.values(commentDrafts.current).forEach((draft) =>
        draft.attachments.forEach((item) => URL.revokeObjectURL(item.previewUrl))
      )
    },
    []
  )

  return (
    <DashboardReviewContextValue.Provider
      value={{
        commentDrafts,
        commentDraftResets,
        resetCommentDraft,
        answerDrafts,
        task,
        contexts,
        filter,
        setFilter,
        subtasks,
        selectedId: selection.id,
        setSelected,
        entry,
        loading,
        error,
        switchContext,
        retry: () => {
          if (pendingContext.current) switchContext(pendingContext.current)
        },
        statusFor,
      }}
    >
      {children}
    </DashboardReviewContextValue.Provider>
  )
}
